import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Typography,
  useTheme,
  styled
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Grid from "@mui/material/Grid2";
import {
  DataGrid,
  GridRowParams
} from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../reduxStore/store";
import { useEffect } from "react";
import React from "react";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { getAuditList, selectedAuditItemDetails, togglreAuditDetailsDrawerState } from "../../reduxStore/getAuditList/getAuditListSlice";
import { GetAuditListReq } from "../../reduxStore/getAuditList/Model";
import { useAppSelector } from "../../reduxStore/hooks";

interface Props {
  isDrawerOpen: boolean | undefined;
}

const drawerWidth = 400;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: -drawerWidth,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  }),
  position: 'relative',
}));

export default function AuditList({ isDrawerOpen }: Props) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const submittedObjRaw = query.get("obj");
  let obj: Record<string, any> = {};
  if (submittedObjRaw) {
    try {
      obj = JSON.parse(submittedObjRaw);
    } catch (error) {
      console.error("Invalid submittedObj JSON", error);
    }
  }

  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const loadingMarkup = <CircularProgress size="30px" />;
  const [page, setPage] = React.useState(1);
  const [pageListCount, setPageListCount] = React.useState("");
  const [listCount, setListCount] = React.useState("");
  const token = localStorage.getItem("access");
  const auditListValue = useAppSelector(
    (state) => state.getAuditListRes
  );
  const [dataGridArray, setDataGridArray] = React.useState([] as any[]);
  const [header, setHeader] = React.useState({} as Record<string, string>);

  const handleRowClick = (params: GridRowParams) => {
    dispatch(selectedAuditItemDetails(params.row))
    dispatch(togglreAuditDetailsDrawerState(true))
  };

  const toHeaderName = (field: string) =>
    field
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

  const isDateField = (field: string) =>
    [
      "dob",
      "modifiedDate"
    ].includes(field);

  const formatDate = (value: string | null | undefined) => {
    if (!value) return "";
    const d = dayjs(value);
    return d.isValid() ? d.format("dddd, MMM DD, YYYY") : value;
  };

  useEffect(() => {
    auditListValue?.response?.numberOfPages !== undefined
      ? setPageListCount(String(auditListValue?.response?.numberOfPages))
      : setPageListCount("0");

    auditListValue?.response?.count !== undefined
      ? setListCount(String(auditListValue?.response?.count))
      : setListCount("0");
    if (auditListValue?.response?.patientList !== undefined) {
      const rows = auditListValue?.response?.patientList;
      const fieldToHeaderMap: Record<string, string> = rows.length
        ? Object.keys(rows[0]).reduce((acc, key) => {
          acc[key] = toHeaderName(key);
          return acc;
        }, {} as Record<string, string>)
        : {};
      setHeader(fieldToHeaderMap);
      setDataGridArray(rows);
      //dispatch(clearGetSearchDocuments());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditListValue]);
  const submittedObj = {
    healthCampId: "10",
    searchKey: obj.searchKey ? obj.searchKey : ""
  };
  useEffect(() => {
    dispatch(
      getAuditList({
        access: token!,
        body: submittedObj,
        pageNo: String(page),
        rowSize: String(rowSize)
      } as GetAuditListReq)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const [rowSize, setRowSize] = React.useState("20");
  const handleChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    dispatch(
      getAuditList({
        access: token!,
        body: submittedObj,
        pageNo: String(value),
        rowSize: String(rowSize)
      } as GetAuditListReq)
    );
  };
  const handleChangeRows = (event: SelectChangeEvent) => {
    setRowSize(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Main open={isDrawerOpen}>
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}
          mb={2}
        >
          <Grid>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "center",
                alignItems: "center"
              }}
              spacing={1}
            >
              <Grid>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  Audit List
                </Typography>
              </Grid>
              <Grid>
                <Chip label={listCount} color="primary" size="small" />
              </Grid>
              <Grid>
                {auditListValue.loading === true
                  ? loadingMarkup
                  : ""}
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            {Number(listCount) > 0 && (
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: "flex-start",
                  alignItems: "center"
                }}
              >
                <Grid>
                  <Typography
                    sx={{
                      color: "text.primary",
                      fontSize: "14px"
                    }}
                  >
                    Rows per page:
                  </Typography>
                </Grid>
                <Grid>
                  <Select
                    id="demo-simple-select-standard"
                    value={rowSize}
                    onChange={handleChangeRows}
                    sx={{
                      "& fieldset": {
                        border: "none"
                      }
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </Grid>
                <Grid>
                  <Typography
                    sx={{
                      color: "text.primary",
                      fontSize: "14px",
                      ml: 2
                    }}
                  >
                    Page: {page}
                  </Typography>
                </Grid>
                <Grid>
                  <Pagination
                    color="primary"
                    count={Number(pageListCount)}
                    page={page}
                    onChange={handleChange}
                    sx={{ color: "text.primary", fontSize: "14px" }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Card
          sx={{
            borderColor: `${theme.palette.divider}`,
            border: "1px solid",
            backgroundColor: `${theme.palette.info.light}`,
            backgroundImage: "none"
          }}
        >
          <CardContent>
            {(auditListValue?.response?.patientList && auditListValue?.response?.patientList.length) ? (
              <DataGrid
                rows={dataGridArray}
                columns={Object.keys(header)
                  .filter(
                    (field) =>
                      field !== "id" &&
                      field !== "profilePicture" &&
                      field !== "createDate" &&
                      field !== "email"
                  )
                  .map((field) => ({
                    field,
                    headerName: header[field],
                    minWidth: 200,
                    flex: 1,
                    valueFormatter: isDateField(field)
                      ? (params: any) => {
                        const val = params;
                        return val ? formatDate(val) : "Not Set";
                      }
                      : undefined
                  }))}
                disableRowSelectionOnClick
                getRowId={(row) => row.patientId}
                onRowClick={handleRowClick}
                sx={{
                  "& .MuiDataGrid-row:hover": {
                    cursor: "pointer"
                  },
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontSize: "16px",
                    fontWeight: "bold"
                  },
                  "&.MuiDataGrid-root": {
                    border: "none"
                  },
                  "& .MuiDataGrid-cell": {
                    fontSize: "16px",
                    fontWeight: "600",
                    borderColor: `${theme.palette.divider}`
                  },
                  "& .MuiDataGrid-row--firstVisible": {
                    borderColor: `${theme.palette.primary.light}`,
                    borderTop: "1px solid"
                  },
                  "& .MuiDataGrid-columnHeader--sortable, .MuiDataGrid-columnHeaderReorder, .MuiDataGrid-filler":
                  {
                    background: `${theme.palette.info.light}`
                  },
                  "& .MuiDataGrid-columnHeader--sortable:hover": {
                    color: `${theme.palette.primary.main}`
                  }
                }}
              />
            ) : null}
          </CardContent>
        </Card>
      </Main>
    </Box>
  );
}